<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useMapGetter } from 'dashboard/composables/store';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import VisitorInfoHub from '../../laisLeads/components/VisitorInfoHub.vue';

const ORIGIN_CONTEXT_QUERY_KEYS = Object.freeze([
  'origin',
  'origem',
  'source',
  'from',
  'click_origin',
  'supply_origin',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'visit_id',
  'visita_id',
  'imovel_id',
  'offer_id',
  'proposal_id',
]);

const route = useRoute();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const { t } = useI18n();
const hubRef = ref(null);

const routeValue = value => {
  if (Array.isArray(value)) return value[0] || '';
  return value || '';
};

const decodeRouteTarget = value => {
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const visitorId = computed(() => routeValue(route.params.visitorId));
const businessContext = computed(
  () =>
    routeValue(route.query.business_context) ||
    routeValue(route.query.businessContext) ||
    backToQueryValue('business_context') ||
    backToQueryValue('businessContext') ||
    'for_rent'
);
const backTo = computed(() => routeValue(route.query.backTo));
const backTarget = computed(
  () =>
    decodeRouteTarget(backTo.value) ||
    frontendURL(`accounts/${accountId.value}/visitas`)
);
const backToQueryValue = key => {
  const target = decodeRouteTarget(backTo.value);
  try {
    return (
      new URL(target, 'https://dashboard.local').searchParams.get(key) || ''
    );
  } catch {
    // Keep relative malformed legacy targets usable; back navigation itself
    // still falls back to the decoded string above.
  }

  const queryString = target.split('?')[1]?.split('#')[0] || '';
  if (!queryString) return '';

  return new URLSearchParams(queryString).get(key) || '';
};
const hubKey = computed(() => `${visitorId.value}:${businessContext.value}`);
const actionContextQuery = computed(() => {
  const context = ORIGIN_CONTEXT_QUERY_KEYS.reduce((query, key) => {
    const value = routeValue(route.query[key]) || backToQueryValue(key);
    if (value) query[key] = value;
    return query;
  }, {});

  context.business_context = businessContext.value;
  if (visitorId.value) context.contact_id = visitorId.value;
  context.backTo = route.fullPath;

  return context;
});

const resetHub = () => {
  hubRef.value?.reset?.();
};

const goBack = () => {
  resetHub();
  router.push(backTarget.value);
};

onBeforeUnmount(resetHub);
</script>

<template>
  <main class="min-h-screen bg-lais-bg">
    <LaisHeader
      :title="t('LEADS.VISITOR_INFO.PAGE_TITLE')"
      :subtitle="t('LEADS.VISITOR_INFO.PAGE_SUBTITLE')"
    >
      <template #actions>
        <button
          type="button"
          class="inline-flex h-10 items-center gap-2 rounded-lais border border-lais-border bg-white px-4 text-lais-label-sm font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary"
          @click="goBack"
        >
          <span class="i-lucide-arrow-left size-4" aria-hidden="true" />
          {{ t('LEADS.VISITOR_INFO.BACK') }}
        </button>
      </template>
    </LaisHeader>

    <VisitorInfoHub
      :key="hubKey"
      ref="hubRef"
      :visitor-id="visitorId"
      :business-context="businessContext"
      :action-context="actionContextQuery"
    />
  </main>
</template>
